/**
 * @description Página de perfil profissional - permite editar foto e descrição pessoal
 * @module PractitionerProfile
 * @since Novembro 2025
 */

import React, { useState, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Slider,
  Paper,
  CircularProgress,
  Switch,
  InputAdornment,
} from "@mui/material";
import {
  Person as PersonIcon,
  Close as CloseIcon,
  PhotoCamera as PhotoCameraIcon,
  Phone as PhoneIcon,
  Instagram as InstagramIcon,
  Visibility as VisibilityIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";

import { LargeContentBox } from "../../components/common";
import { PageHeader } from "../../components/common/PageHeader";
import { useSessionContext } from "@/contexts/SessionContext";
import { CardProfessional } from "./components/CardProfessional";
import EditPractitionerRoleDialog from "./components/EditPractitionerRoleDialog";
import { CardContact } from "./components/CardContact";
import { CardPassword } from "./components/CardPassword";
import { EditPhoneDialog } from "./components/EditPhoneDialog";
import { EditPasswordDialog } from "./components/EditPasswordDialog";
import EditEmailDialog from "./components/EditEmailDialog";
import { GoogleAuthCard } from "./components/GoogleAuthCard";
import { usePractitionerRoleContext } from "@/contexts/PractitionerRoleContext";
import { PatchPractitioner } from "@/services/fhir/practitioner/PatchPractitioner";

// Tamanho do crop circular
const CROP_SIZE = 220;

// Limite de caracteres para a descrição
const MAX_DESCRIPTION_LENGTH = 500;

export function PractitionerProfile() {
  const { loading, roles } = useSessionContext();
  const { userPractitioner, refetchPractitionerRole } =
    usePractitionerRoleContext();

  // ========== Estados da foto ==========
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Armazena APENAS uma nova foto pendente de salvamento (não salva ainda)
  const [newPhotoBase64, setNewPhotoBase64] = useState<string | null>(null);

  // Dados derivados diretamente do servidor
  const serverPhoto = userPractitioner?.photo?.[0];
  const serverPhotoBase64 = serverPhoto?.data || null;
  const serverPhotoUrl = serverPhotoBase64
    ? `data:${
        serverPhoto?.contentType || "image/png"
      };base64,${serverPhotoBase64}`
    : null;

  // Foto atual: usa a nova foto se houver, senão usa a do servidor
  const currentPhotoBase64 = newPhotoBase64 ?? serverPhotoBase64;
  const currentPhotoUrl = newPhotoBase64
    ? `data:image/png;base64,${newPhotoBase64}`
    : serverPhotoUrl;

  // Estados do dialog de crop
  const [cropOpen, setCropOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const [cropScale, setCropScale] = useState<number>(1);
  const [cropOffsetY, setCropOffsetY] = useState<number>(0);
  const [cropOffsetX, setCropOffsetX] = useState<number>(0);

  // ========== URLs das Extensions ==========
  const EXTENSION_DESCRIPTION_URL =
    "http://fluxmed.com.br/fhir/StructureDefinition/practitioner-description";
  const EXTENSION_PROFILE_EXPOSURE_URL =
    "http://fluxmed.com.br/fhir/StructureDefinition/practitioner-profile-exposure";
  const EXTENSION_SCHEDULE_PRESENTATION_URL =
    "http://fluxmed.com.br/fhir/StructureDefinition/practitioner-schedule-presentation";

  // ========== Descrição - dados derivados do servidor ==========
  // Encontra a descrição no array de extensions do Practitioner
  const serverDescriptionIndex =
    userPractitioner?.extension?.findIndex(
      (ext) => ext.url === EXTENSION_DESCRIPTION_URL
    ) ?? -1;
  const serverDescription =
    serverDescriptionIndex >= 0
      ? userPractitioner?.extension?.[serverDescriptionIndex]?.valueString || ""
      : "";

  // Estado para descrição pendente de salvamento (null = sem alteração)
  const [newDescription, setNewDescription] = useState<string | null>(null);

  // Descrição atual: usa a nova se houver edição, senão usa a do servidor
  const currentDescription = newDescription ?? serverDescription;
  // Verifica se a descrição foi alterada
  const descriptionChanged =
    newDescription !== null && newDescription !== serverDescription;

  // ========== Exposição do Perfil - dados derivados do servidor ==========
  // Encontra a configuração de exposição no array de extensions
  const serverExposureIndex =
    userPractitioner?.extension?.findIndex(
      (ext) => ext.url === EXTENSION_PROFILE_EXPOSURE_URL
    ) ?? -1;
  const serverExposure =
    serverExposureIndex >= 0
      ? userPractitioner?.extension?.[serverExposureIndex]?.valueBoolean ??
        false
      : false;

  // Switch salva na hora (após dialog). Valor refletido = servidor (sem pending).
  const currentExposure = serverExposure;

  // ========== Apresentação de Horários - dados derivados do servidor ==========
  // Encontra a configuração de apresentação de horários no array de extensions
  // Por padrão, se a extensão não existir, assume true (horários serão exibidos)
  const serverSchedulePresentationIndex =
    userPractitioner?.extension?.findIndex(
      (ext) => ext.url === EXTENSION_SCHEDULE_PRESENTATION_URL
    ) ?? -1;
  const serverSchedulePresentation =
    serverSchedulePresentationIndex >= 0
      ? userPractitioner?.extension?.[serverSchedulePresentationIndex]
          ?.valueBoolean ?? true // Padrão: true (exibir horários)
      : true; // Se a extensão não existe, padrão é true (exibir horários)

  // Switch salva na hora (após dialog). Valor refletido = servidor (sem pending).
  const currentSchedulePresentation = serverSchedulePresentation;

  // Dialog de confirmação dos switches (evita usuário esquecer de clicar em Salvar)
  type SwitchConfirmType = "exposure" | "schedulePresentation";
  const [switchConfirm, setSwitchConfirm] = useState<{
    type: SwitchConfirmType;
    proposedValue: boolean;
  } | null>(null);
  const [switchSaving, setSwitchSaving] = useState(false);

  // ========== Telefone - dados derivados do servidor ==========
  // Encontra o telefone público no telecom do Practitioner (use: "work" ou "mobile")
  const serverPhoneIndex =
    userPractitioner?.telecom?.findIndex(
      (t) => t.system === "phone" && (t.use === "work" || t.use === "mobile")
    ) ?? -1;
  const serverPhone =
    serverPhoneIndex >= 0
      ? userPractitioner?.telecom?.[serverPhoneIndex]?.value || ""
      : "";

  // Estado para telefone pendente de salvamento (null = sem alteração)
  const [newPublicPhone, setNewPublicPhone] = useState<string | null>(null);

  // Telefone atual: usa o novo se houver edição, senão usa o do servidor
  const currentPhone = newPublicPhone ?? serverPhone;
  // Verifica se o telefone foi alterado
  const phoneChanged =
    newPublicPhone !== null && newPublicPhone !== serverPhone;

  // ========== Instagram - dados derivados do servidor ==========
  // Sistema/URI usado para identificar o Instagram nos identifiers
  const INSTAGRAM_SYSTEM = "https://instagram.com";

  // Encontra o Instagram no array de identifiers do Practitioner
  const serverInstagramIndex =
    userPractitioner?.identifier?.findIndex(
      (id) => id.system === INSTAGRAM_SYSTEM
    ) ?? -1;
  const serverInstagram =
    serverInstagramIndex >= 0
      ? userPractitioner?.identifier?.[serverInstagramIndex]?.value || ""
      : "";

  // Estado para Instagram pendente de salvamento (null = sem alteração)
  const [newInstagram, setNewInstagram] = useState<string | null>(null);

  // Instagram atual: usa o novo se houver edição, senão usa o do servidor
  const currentInstagram = newInstagram ?? serverInstagram;
  // Verifica se o Instagram foi alterado
  const instagramChanged =
    newInstagram !== null && newInstagram !== serverInstagram;

  // ========== Estado de loading ==========
  const [saving, setSaving] = useState(false);

  // ========== Handlers da foto ==========

  /**
   * Abre o seletor de arquivos
   */
  const handleSelectPhoto = () => {
    fileInputRef.current?.click();
  };

  /**
   * Processa o arquivo selecionado e abre o dialog de crop
   */
  const handleFileChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Valida tipo de arquivo
    const allowedTypes = ["image/png", "image/jpeg"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Formato não suportado. Use PNG ou JPG.");
      e.target.value = "";
      return;
    }

    // Lê o arquivo como Data URL
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      setCropImageSrc(result);
      setCropScale(1);
      setCropOffsetX(0);
      setCropOffsetY(0);
      setCropOpen(true);
    };
    reader.readAsDataURL(file);
  };

  /**
   * Gera a imagem recortada circular
   */
  const generateCircularCroppedImage = async (
    imageSrc: string,
    scale: number,
    offsetY: number,
    offsetX: number
  ): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          const size = CROP_SIZE;
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext("2d");
          if (!ctx) return reject(new Error("Canvas context não disponível"));

          // Limpa e cria máscara circular
          ctx.clearRect(0, 0, size, size);
          ctx.save();
          ctx.beginPath();
          ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
          ctx.closePath();
          ctx.clip();

          // Calcula dimensões para desenhar a imagem
          const imgRatio = img.width / img.height;
          const baseWidth = imgRatio >= 1 ? size : size * imgRatio;
          const baseHeight = imgRatio >= 1 ? size / imgRatio : size;
          const drawWidth = baseWidth * scale;
          const drawHeight = baseHeight * scale;
          const dx = (size - drawWidth) / 2 + offsetX;
          const dy = (size - drawHeight) / 2 + offsetY;

          ctx.drawImage(img, dx, dy, drawWidth, drawHeight);
          ctx.restore();

          const dataUrl = canvas.toDataURL("image/png");
          resolve(dataUrl);
        } catch (e) {
          reject(e);
        }
      };
      img.onerror = (e) => reject(e as unknown as Error);
      img.src = imageSrc;
    });
  };

  /**
   * Confirma o crop e define a nova foto pendente
   */
  const handleConfirmCrop = async () => {
    if (!cropImageSrc) return;
    try {
      const croppedDataUrl = await generateCircularCroppedImage(
        cropImageSrc,
        cropScale,
        cropOffsetY,
        cropOffsetX
      );
      // Extrai apenas o base64 (sem o prefixo data:image/png;base64,)
      const base64 = croppedDataUrl.split(",")[1] || "";
      setNewPhotoBase64(base64);
      setCropOpen(false);
      toast.success("Foto ajustada! Clique em Salvar para confirmar.");
    } catch (e) {
      toast.error("Erro ao processar a imagem.");
      setCropOpen(false);
    }
  };

  /**
   * Cancela o crop e limpa estados
   */
  const handleCancelCrop = () => {
    setCropOpen(false);
    setCropImageSrc(null);
    setCropScale(1);
    setCropOffsetX(0);
    setCropOffsetY(0);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Estado de loading para remoção da foto
  const [removingPhoto, setRemovingPhoto] = useState(false);

  /**
   * Remove a foto - se há foto pendente, apenas limpa o estado
   * Se há foto no servidor, faz PATCH para remover
   */
  const handleRemovePhoto = async () => {
    // Se há apenas foto pendente (não salva), apenas limpa o estado
    if (newPhotoBase64 && !serverPhotoBase64) {
      setNewPhotoBase64(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      toast.info("Foto removida.");
      return;
    }

    // Se há foto no servidor, faz PATCH para remover
    if (serverPhotoBase64 && userPractitioner?.id) {
      setRemovingPhoto(true);
      try {
        await PatchPractitioner({
          practitionerId: userPractitioner.id,
          operation: "remove",
          path: "/photo/0",
        });

        // Atualiza o contexto para refletir a remoção
        refetchPractitionerRole();
        // Limpa também qualquer foto pendente
        setNewPhotoBase64(null);
        if (fileInputRef.current) fileInputRef.current.value = "";

        toast.success("Foto removida com sucesso!");
      } catch (error) {
        console.error("Erro ao remover foto:", error);
        toast.error("Erro ao remover a foto. Tente novamente.");
      } finally {
        setRemovingPhoto(false);
      }
    }
  };

  // ========== Handlers dos switches (confirmação + save imediato) ==========

  /**
   * Persiste exposição do perfil (mesma regra que antes estava no handleSave).
   */
  const saveExposure = async (value: boolean) => {
    if (!userPractitioner?.id) return;

    const exposureExtension: fhir5.Extension = {
      url: EXTENSION_PROFILE_EXPOSURE_URL,
      valueBoolean: value,
    };

    if (serverExposureIndex >= 0) {
      await PatchPractitioner({
        practitionerId: userPractitioner.id,
        operation: "replace",
        path: `/extension/${serverExposureIndex}`,
        content: exposureExtension,
      });
    } else {
      const hasExtension =
        userPractitioner.extension && userPractitioner.extension.length > 0;
      await PatchPractitioner({
        practitionerId: userPractitioner.id,
        operation: "add",
        path: hasExtension ? "/extension/-" : "/extension",
        content: hasExtension ? exposureExtension : [exposureExtension],
      });
    }
  };

  /**
   * Persiste apresentação de horários (true = padrão → remove extensão).
   */
  const saveSchedulePresentation = async (value: boolean) => {
    if (!userPractitioner?.id) return;

    if (value === true) {
      if (serverSchedulePresentationIndex >= 0) {
        await PatchPractitioner({
          practitionerId: userPractitioner.id,
          operation: "remove",
          path: `/extension/${serverSchedulePresentationIndex}`,
        });
      }
      return;
    }

    const schedulePresentationExtension: fhir5.Extension = {
      url: EXTENSION_SCHEDULE_PRESENTATION_URL,
      valueBoolean: false,
    };

    if (serverSchedulePresentationIndex >= 0) {
      await PatchPractitioner({
        practitionerId: userPractitioner.id,
        operation: "replace",
        path: `/extension/${serverSchedulePresentationIndex}`,
        content: schedulePresentationExtension,
      });
    } else {
      const hasExtension =
        userPractitioner.extension && userPractitioner.extension.length > 0;
      await PatchPractitioner({
        practitionerId: userPractitioner.id,
        operation: "add",
        path: hasExtension ? "/extension/-" : "/extension",
        content: hasExtension
          ? schedulePresentationExtension
          : [schedulePresentationExtension],
      });
    }
  };

  const openSwitchConfirm = (
    type: SwitchConfirmType,
    proposedValue: boolean
  ) => {
    setSwitchConfirm({ type, proposedValue });
  };

  const handleCancelSwitchConfirm = () => {
    if (switchSaving) return;
    setSwitchConfirm(null);
  };

  const handleConfirmSwitch = async () => {
    if (!switchConfirm) return;
    setSwitchSaving(true);
    try {
      if (switchConfirm.type === "exposure") {
        await saveExposure(switchConfirm.proposedValue);
        toast.success(
          switchConfirm.proposedValue
            ? "Exposição do perfil ativada."
            : "Exposição do perfil desativada."
        );
      } else {
        await saveSchedulePresentation(switchConfirm.proposedValue);
        toast.success(
          switchConfirm.proposedValue
            ? "Exibição de horários ativada."
            : "Exibição de horários desativada."
        );
      }
      refetchPractitionerRole();
      setSwitchConfirm(null);
    } catch (error) {
      console.error("Erro ao salvar switch de visibilidade:", error);
      toast.error("Não foi possível salvar. Tente novamente.");
    } finally {
      setSwitchSaving(false);
    }
  };

  // Textos do dialog conforme o switch e o valor proposto
  const switchConfirmCopy = (() => {
    if (!switchConfirm) return { title: "", body: "" };
    if (switchConfirm.type === "exposure") {
      return switchConfirm.proposedValue
        ? {
            title: "Ativar exposição do perfil?",
            body: "Seu perfil poderá ser exibido publicamente para pacientes e outros profissionais.",
          }
        : {
            title: "Desativar exposição do perfil?",
            body: "Seu perfil deixará de ser exibido publicamente. A opção de horários também ficará oculta enquanto a exposição estiver desativada.",
          };
    }
    return switchConfirm.proposedValue
      ? {
          title: "Exibir horários disponíveis?",
          body: "Os pacientes verão os horários disponíveis e poderão agendar pelo horário específico.",
        }
      : {
          title: "Ocultar horários disponíveis?",
          body: "Os pacientes verão apenas os dias disponíveis e solicitarão o agendamento. Você confirmará o horário depois.",
        };
  })();

  // ========== Handler de salvar ==========

  /**
   * Salva as alterações do perfil (foto e telefone)
   * Usa PATCH para atualizar o Practitioner no servidor FHIR
   * Switches de visibilidade salvam no dialog próprio (não passam por aqui).
   */
  const handleSave = async () => {
    if (!userPractitioner?.id) return;
    setSaving(true);

    try {
      // ===== Salva a foto se houver uma nova pendente =====
      if (newPhotoBase64) {
        const photoAttachment: fhir5.Attachment = {
          contentType: "image/png",
          data: newPhotoBase64,
          title: "Foto de perfil",
          creation: new Date().toISOString(),
        };

        const operation = serverPhotoBase64 ? "replace" : "add";
        const path = serverPhotoBase64 ? "/photo/0" : "/photo";
        const content = serverPhotoBase64 ? photoAttachment : [photoAttachment];

        await PatchPractitioner({
          practitionerId: userPractitioner.id,
          operation,
          path,
          content,
        });

        setNewPhotoBase64(null);
      }

      // ===== Salva o telefone se foi alterado =====
      if (phoneChanged) {
        const phoneValue = newPublicPhone?.trim() || "";

        if (phoneValue === "" && serverPhoneIndex >= 0) {
          // Remove o telefone existente
          await PatchPractitioner({
            practitionerId: userPractitioner.id,
            operation: "remove",
            path: `/telecom/${serverPhoneIndex}`,
          });
        } else if (phoneValue !== "") {
          // Monta o ContactPoint FHIR para o telefone
          const phoneContact: fhir5.ContactPoint = {
            system: "phone",
            value: phoneValue,
            use: "mobile",
          };

          if (serverPhoneIndex >= 0) {
            // Atualiza o telefone existente
            await PatchPractitioner({
              practitionerId: userPractitioner.id,
              operation: "replace",
              path: `/telecom/${serverPhoneIndex}`,
              content: phoneContact,
            });
          } else {
            // Adiciona novo telefone
            // Verifica se já existe telecom array
            const hasTelecom =
              userPractitioner.telecom && userPractitioner.telecom.length > 0;
            await PatchPractitioner({
              practitionerId: userPractitioner.id,
              operation: "add",
              path: hasTelecom ? "/telecom/-" : "/telecom",
              content: hasTelecom ? phoneContact : [phoneContact],
            });
          }
        }

        setNewPublicPhone(null);
      }

      // ===== Salva o Instagram se foi alterado =====
      if (instagramChanged) {
        const instagramValue = newInstagram?.trim() || "";

        if (instagramValue === "" && serverInstagramIndex >= 0) {
          // Remove o Instagram existente
          await PatchPractitioner({
            practitionerId: userPractitioner.id,
            operation: "remove",
            path: `/identifier/${serverInstagramIndex}`,
          });
        } else if (instagramValue !== "") {
          // Monta o Identifier FHIR para o Instagram
          const instagramIdentifier: fhir5.Identifier = {
            system: INSTAGRAM_SYSTEM,
            value: instagramValue,
            use: "secondary",
          };

          if (serverInstagramIndex >= 0) {
            // Atualiza o Instagram existente
            await PatchPractitioner({
              practitionerId: userPractitioner.id,
              operation: "replace",
              path: `/identifier/${serverInstagramIndex}`,
              content: instagramIdentifier,
            });
          } else {
            // Adiciona novo Instagram
            // Verifica se já existe identifier array
            const hasIdentifier =
              userPractitioner.identifier &&
              userPractitioner.identifier.length > 0;
            await PatchPractitioner({
              practitionerId: userPractitioner.id,
              operation: "add",
              path: hasIdentifier ? "/identifier/-" : "/identifier",
              content: hasIdentifier
                ? instagramIdentifier
                : [instagramIdentifier],
            });
          }
        }

        setNewInstagram(null);
      }

      // ===== Salva a descrição se foi alterada =====
      if (descriptionChanged) {
        const descriptionValue = newDescription?.trim() || "";

        if (descriptionValue === "" && serverDescriptionIndex >= 0) {
          // Remove a descrição existente
          await PatchPractitioner({
            practitionerId: userPractitioner.id,
            operation: "remove",
            path: `/extension/${serverDescriptionIndex}`,
          });
        } else if (descriptionValue !== "") {
          // Monta a Extension FHIR para a descrição
          const descriptionExtension: fhir5.Extension = {
            url: EXTENSION_DESCRIPTION_URL,
            valueString: descriptionValue,
          };

          if (serverDescriptionIndex >= 0) {
            // Atualiza a descrição existente
            await PatchPractitioner({
              practitionerId: userPractitioner.id,
              operation: "replace",
              path: `/extension/${serverDescriptionIndex}`,
              content: descriptionExtension,
            });
          } else {
            // Adiciona nova descrição
            const hasExtension =
              userPractitioner.extension &&
              userPractitioner.extension.length > 0;
            await PatchPractitioner({
              practitionerId: userPractitioner.id,
              operation: "add",
              path: hasExtension ? "/extension/-" : "/extension",
              content: hasExtension
                ? descriptionExtension
                : [descriptionExtension],
            });
          }
        }

        setNewDescription(null);
      }

      // Atualiza o contexto para refletir as mudanças
      refetchPractitionerRole();
      toast.success("Alterações salvas com sucesso!");
    } catch (error) {
      console.error("Erro ao salvar:", error);
      toast.error("Erro ao salvar. Tente novamente.");
    } finally {
      setSaving(false);
    }
  };

  // Verifica se há alterações para salvar (switches salvam no dialog, não entram aqui)
  const hasChanges =
    newPhotoBase64 !== null ||
    phoneChanged ||
    instagramChanged ||
    descriptionChanged;

  const [selectedPractitionerRole, setSelectPractitionerRole] = useState<any>();
  const [isEditPracititionerRolesOpen, setIsEditPracititionerRolesOpen] =
    useState<boolean>(false);

  const [isEditEmailOpen, setIsEditEmailOpen] = useState<boolean>(false);
  const [isEditPhoneOpen, setIsEditPhoneOpen] = useState<boolean>(false);
  const [isEditPasswordOpen, setIsEditPasswordOpen] = useState<boolean>(false);

  if (loading || !roles)
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );

  return (
    <LargeContentBox>
      <Stack spacing={3} width="100%">
        {/* Header da página */}
        <PageHeader
          title="Perfil"
          icon={PersonIcon}
          centered
          description="Informações pessoais e opções de gerenciamento do seu perfil nos serviços Fluxmed"
        />

        {/* Seção: Descrição Pessoal */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            border: "1px solid",
            borderColor: "grey.200",
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" fontWeight={500} mb={2}>
            Foto
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {/* Avatar circular */}
            <Box
              sx={{
                width: 100,
                height: 100,
                borderRadius: "50%",
                overflow: "hidden",
                bgcolor: "grey.100",
                border: "2px solid",
                borderColor: "grey.300",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {currentPhotoUrl ? (
                <img
                  src={currentPhotoUrl}
                  alt="Foto do profissional"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <PersonIcon sx={{ color: "grey.400", fontSize: 48 }} />
              )}
            </Box>

            {/* Botões e instruções */}
            <Box>
              <Stack direction="row" spacing={1} mb={0.5}>
                <Button
                  variant="outlined"
                  startIcon={<PhotoCameraIcon />}
                  onClick={handleSelectPhoto}
                  size="small"
                >
                  {currentPhotoUrl ? "Trocar foto" : "Adicionar foto"}
                </Button>

                {currentPhotoUrl && (
                  <Button
                    variant="text"
                    color="error"
                    onClick={handleRemovePhoto}
                    size="small"
                    disabled={removingPhoto}
                  >
                    {removingPhoto ? "Removendo..." : "Remover"}
                  </Button>
                )}
              </Stack>

              {/* Input file oculto */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />

              <Typography variant="caption" color="text.secondary">
                Formatos aceitos: PNG ou JPG
              </Typography>
            </Box>
          </Box>

          {/* ========== Seção: Contato e Redes Sociais ========== */}
          <Typography variant="h6" fontWeight={500} mt={3} mb={2}>
            Contato e Redes Sociais
          </Typography>

          <Stack spacing={2} mb={3}>
            {/* Campo de telefone público */}
            <TextField
              fullWidth
              label="Telefone para contato"
              placeholder="(11) 99999-9999"
              value={currentPhone}
              onChange={(e) => setNewPublicPhone(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon sx={{ color: "grey.500" }} />
                  </InputAdornment>
                ),
              }}
              helperText={
                phoneChanged
                  ? "Telefone alterado - clique em Salvar para confirmar"
                  : "Telefone que será exibido no seu perfil público"
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />

            {/* Campo de Instagram */}
            <TextField
              fullWidth
              label="Instagram"
              placeholder="seu_usuario"
              value={currentInstagram}
              onChange={(e) => {
                // Remove @ se o usuário digitar
                const value = e.target.value.replace(/^@/, "");
                setNewInstagram(value);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <InstagramIcon sx={{ color: "grey.500" }} />
                    <Typography sx={{ ml: 0.5, color: "grey.500" }}>
                      @
                    </Typography>
                  </InputAdornment>
                ),
              }}
              helperText={
                instagramChanged
                  ? "Instagram alterado - clique em Salvar para confirmar"
                  : "Seu nome de usuário no Instagram (sem o @)"
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />
          </Stack>

          {/* ========== Seção: Visibilidade do Perfil ========== */}
          <Typography variant="h6" fontWeight={500} mb={1}>
            Visibilidade do Perfil
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              p: 2,
              bgcolor: "grey.50",
              borderRadius: 2,
              border: "1px solid",
              borderColor: "grey.200",
              // Margem menor se vai aparecer o switch de horários
              mb: currentExposure ? 2 : 3,
            }}
          >
            <VisibilityIcon sx={{ color: "grey.600", mr: 2 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body1" fontWeight={500}>
                Permitir exposição do perfil
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Quando ativado, seu perfil poderá ser exibido publicamente para
                pacientes e outros profissionais
              </Typography>
            </Box>
            <Switch
              checked={currentExposure}
              // Abre dialog; só persiste após confirmar (evita esquecer Salvar)
              onChange={(e) =>
                openSwitchConfirm("exposure", e.target.checked)
              }
              disabled={switchSaving}
              color="primary"
            />
          </Box>

          {/* Switch de apresentação de horários - só aparece se exposição estiver habilitada */}
          {currentExposure && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                p: 2,
                bgcolor: "grey.50",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "grey.200",
                mb: 3,
              }}
            >
              <ScheduleIcon sx={{ color: "grey.600", mr: 2 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body1" fontWeight={500}>
                  Exibir horários disponíveis
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {currentSchedulePresentation ? (
                    <>
                      <strong>Ativado:</strong> Os pacientes verão os horários
                      disponíveis e poderão agendar consultas diretamente pelo
                      horário específico.
                    </>
                  ) : (
                    <>
                      <strong>Desativado:</strong> Os pacientes verão apenas os
                      dias disponíveis e solicitarão o agendamento. Você
                      confirmará o horário específico posteriormente.
                    </>
                  )}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    fontStyle: "italic",
                    mt: 0.5,
                    color: "grey.600",
                  }}
                >
                  Por padrão, ao liberar sua agenda, os horários estarão
                  disponíveis. Você pode desativar esta opção para exibir apenas
                  os dias disponíveis.
                </Typography>
              </Box>
              <Switch
                checked={currentSchedulePresentation}
                onChange={(e) =>
                  openSwitchConfirm("schedulePresentation", e.target.checked)
                }
                disabled={switchSaving}
                color="primary"
              />
            </Box>
          )}

          <Typography variant="h6" fontWeight={500} mb={2}>
            Descrição Pessoal
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Conte um pouco sobre você, sua formação e experiência profissional..."
            value={currentDescription}
            onChange={(e) => {
              // Limita o tamanho da descrição
              if (e.target.value.length <= MAX_DESCRIPTION_LENGTH) {
                setNewDescription(e.target.value);
              }
            }}
            helperText={
              descriptionChanged
                ? `${currentDescription.length}/${MAX_DESCRIPTION_LENGTH} - Alterado, clique em Salvar`
                : `${currentDescription.length}/${MAX_DESCRIPTION_LENGTH} caracteres`
            }
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
          />

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!hasChanges || saving}
              sx={{
                bgcolor: "#00A884",
                "&:hover": { bgcolor: "#008c6e" },
                borderRadius: 2,
                px: 4,
              }}
            >
              {saving ? "Salvando..." : "Salvar alterações"}
            </Button>
          </Box>
        </Paper>

        <CardPassword onClick={() => setIsEditPasswordOpen(true)} />

        <CardContact
          setIsEditEmailOpen={setIsEditEmailOpen}
          setIsEditPhoneOpen={setIsEditPhoneOpen}
        />

        <CardProfessional
          setSelectPractitionerRole={setSelectPractitionerRole}
          setIsEditPracititionerRolesOpen={setIsEditPracititionerRolesOpen}
        />

        {/* Integração com Google Calendar */}
        <GoogleAuthCard />
      </Stack>

      {/* ========== Dialog de Ajuste da Foto ========== */}

      {cropOpen && (
        <Dialog
          open={cropOpen}
          onClose={(_, reason) => {
            if (reason !== "backdropClick") handleCancelCrop();
          }}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="h6">Ajustar foto</Typography>
              <IconButton
                onClick={handleCancelCrop}
                size="small"
                sx={{ color: "grey.600" }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent>
            <Stack spacing={2} alignItems="center">
              {/* Preview circular */}
              <Box
                sx={{
                  width: CROP_SIZE,
                  height: CROP_SIZE,
                  borderRadius: "50%",
                  overflow: "hidden",
                  border: "2px solid",
                  borderColor: "#e0e0e0",
                  bgcolor: "grey.100",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {cropImageSrc ? (
                  <img
                    src={cropImageSrc}
                    alt="Pré-visualização de recorte"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      transform: `translate3d(${cropOffsetX}px, ${cropOffsetY}px, 0) scale(${cropScale})`,
                      transformOrigin: "center",
                    }}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Carregando imagem...
                  </Typography>
                )}
              </Box>

              {/* Slider de Zoom */}
              <Box sx={{ width: "100%", px: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Zoom
                </Typography>
                <Slider
                  min={0.5}
                  max={3}
                  step={0.01}
                  value={cropScale}
                  onChange={(_, val) => setCropScale(val as number)}
                />
              </Box>

              {/* Slider de Posição Vertical */}
              <Box sx={{ width: "100%", px: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Posição vertical
                </Typography>
                <Slider
                  min={-100}
                  max={100}
                  step={1}
                  value={cropOffsetY}
                  onChange={(_, val) => setCropOffsetY(val as number)}
                />
              </Box>

              {/* Slider de Posição Horizontal */}
              <Box sx={{ width: "100%", px: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Posição horizontal
                </Typography>
                <Slider
                  min={-100}
                  max={100}
                  step={1}
                  value={cropOffsetX}
                  onChange={(_, val) => setCropOffsetX(val as number)}
                />
              </Box>
            </Stack>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleCancelCrop}>Cancelar</Button>
            <Button
              variant="contained"
              onClick={handleConfirmCrop}
              sx={{ bgcolor: "#00A884", "&:hover": { bgcolor: "#008c6e" } }}
              disabled={!cropImageSrc}
            >
              Aplicar
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Dialog: confirma e salva switch de visibilidade na hora */}
      {switchConfirm && (
        <Dialog
          open={Boolean(switchConfirm)}
          onClose={handleCancelSwitchConfirm}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>{switchConfirmCopy.title}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              {switchConfirmCopy.body}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelSwitchConfirm} disabled={switchSaving}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmSwitch}
              disabled={switchSaving}
              sx={{ bgcolor: "#00A884", "&:hover": { bgcolor: "#008c6e" } }}
            >
              {switchSaving ? "Salvando..." : "Confirmar"}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {selectedPractitionerRole?.roles !== undefined && (
        <EditPractitionerRoleDialog
          isOpen={isEditPracititionerRolesOpen}
          setSelectedRole={setSelectPractitionerRole}
          seletedRole={selectedPractitionerRole}
          setIsOpen={setIsEditPracititionerRolesOpen}
        />
      )}

      {isEditEmailOpen && (
        <EditEmailDialog
          isOpen={isEditEmailOpen}
          setIsOpen={setIsEditEmailOpen}
        />
      )}

      {isEditPhoneOpen && (
        <EditPhoneDialog
          isOpen={isEditPhoneOpen}
          setIsOpen={setIsEditPhoneOpen}
        />
      )}

      {isEditPasswordOpen && (
        <EditPasswordDialog
          isOpen={isEditPasswordOpen}
          setIsOpen={setIsEditPasswordOpen}
        />
      )}
    </LargeContentBox>
  );
}
